import {
	auditService,
	casesLawyerService,
	GET_CASE_STEPS_STATUS,
	GET_LAWYER_CASE_DETAILS,
} from '@/api';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import * as React from 'react';
import { PhaseName, StepStatusCaseData } from '@/interfaces';
import { getValidCaseId } from '@/utils';

const getOptionalStep = (phase: PhaseName): string[] => {
	switch (phase) {
		case 'Applicant Intake':
			return ['Documents Requested (Optional)'];
		case 'Active Case':
			return ['Opt Out Request'];
	}
	return [];
};

const checkPhaseCompleted = (
	steps: StepStatusCaseData | undefined,
	phase: PhaseName
): boolean => {
	if (!steps || !phase) return false;

	const phaseSteps = steps.status.find((step: any) => step.fase_name === phase);

	if (!phaseSteps) return false;

	const stepException = getOptionalStep(phase);

	let isApproved: boolean;

	if (stepException.length > 0) {
		isApproved = phaseSteps.steps
			.filter((s: any) => !stepException.includes(s.name))
			.every((step: any) => step.status === 'approved');
	} else {
		isApproved = phaseSteps.steps.every(
			(step: any) => step.status === 'approved'
		);
	}

	return isApproved || phaseSteps.finish_date !== null;
};

export const useCaseStep = () => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);

	const { data: caseSteps, isLoading } = useQuery({
		queryKey: [GET_CASE_STEPS_STATUS, caseId],
		queryFn: () => auditService.getStepStatus(caseId as string),
		refetchOnWindowFocus: false,
		enabled: !!caseId,
	});

	const { data: caseLawyerDetails } = useQuery({
		queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
		queryFn: () => casesLawyerService.getCaseDetails(caseId as string),
		enabled: !!caseId,
	});

	// Check if case is resolved
	const checkIsCaseResolved = React.useCallback((): boolean => {
		return (
			caseLawyerDetails?.data.progress_name === 'Resolved Case' ||
			caseLawyerDetails?.data.progress_name === 'Case Resolved'
		);
	}, [caseLawyerDetails]);

	// Síncrona — usa la data cacheada del query
	const isPhaseCompleted = React.useCallback(
		(phase: PhaseName): boolean => {
			if (checkIsCaseResolved()) return true;
			return checkPhaseCompleted(caseSteps?.data, phase);
		},
		[caseSteps, checkIsCaseResolved]
	);

	// Asíncrona — recibe un id, hace fetch y evalúa
	const isPhaseCompletedById = React.useCallback(
		async (
			steps: StepStatusCaseData | undefined,
			phase: PhaseName,
			currentId: string
		): Promise<boolean> => {
			return checkPhaseCompleted(steps, phase);
		},
		[]
	);

	return {
		caseSteps,
		isLoading,
		isPhaseCompleted,
		isPhaseCompletedById,
		checkIsCaseResolved,
	};
};
